// Enhanced logger with visual styling and timestamps
type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogStyles {
  badge: string;
  text: string;
}

const LOG_STYLES: Record<LogLevel, LogStyles> = {
  info: {
    badge: 'background: #2563eb; color: white; padding: 2px 6px; border-radius: 4px;',
    text: 'color: #2563eb;'
  },
  warn: {
    badge: 'background: #f59e0b; color: white; padding: 2px 6px; border-radius: 4px;',
    text: 'color: #f59e0b;'
  },
  error: {
    badge: 'background: #dc2626; color: white; padding: 2px 6px; border-radius: 4px;',
    text: 'color: #dc2626;'
  },
  debug: {
    badge: 'background: #6b7280; color: white; padding: 2px 6px; border-radius: 4px;',
    text: 'color: #6b7280;'
  }
};

function formatMessage(level: LogLevel, message: string): [string, string[]] {
  const timestamp = new Date().toLocaleTimeString();
  const styles = LOG_STYLES[level];
  
  return [
    `%c${level.toUpperCase()}%c ${timestamp} %c${message}`,
    [styles.badge, 'color: gray;', styles.text]
  ];
}

function formatContext(context?: Record<string, unknown>): string {
  if (!context) return '';
  try {
    return JSON.stringify(context, null, 2);
  } catch (err) {
    return '[Unable to stringify context]';
  }
}

export const logger = {
  error: (message: string, error?: unknown, context?: Record<string, unknown>) => {
    const [formattedMessage, styles] = formatMessage('error', message);
    console.error(formattedMessage, ...styles);
    
    if (error) {
      console.error(
        error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : error
      );
    }
    
    if (context) {
      console.error(formatContext(context));
    }
  },
  
  warn: (message: string, context?: Record<string, unknown>) => {
    const [formattedMessage, styles] = formatMessage('warn', message);
    console.warn(formattedMessage, ...styles);
    
    if (context) {
      console.warn(formatContext(context));
    }
  },
  
  info: (message: string, context?: Record<string, unknown>) => {
    const [formattedMessage, styles] = formatMessage('info', message);
    console.log(formattedMessage, ...styles);
    
    if (context) {
      console.log(formatContext(context));
    }
  },
  
  debug: (message: string, context?: Record<string, unknown>) => {
    if (import.meta.env.DEV) {
      const [formattedMessage, styles] = formatMessage('debug', message);
      console.log(formattedMessage, ...styles);
      
      if (context) {
        console.log(formatContext(context));
      }
    }
  }
};